import * as React from "react";

import { cn } from "~/lib/utils";

interface WavyDividerProps extends React.SVGAttributes<SVGSVGElement> {
  /** Width in px. Production PDP articles header uses 139. */
  width?: number;
  /** Height in px. The SVG viewBox is clipped to this height to expose only the top wave. */
  height?: number;
}

/**
 * Decorative wavy divider line. Colored via the per-site `--wavy` CSS variable
 * (exposed as `text-wavy`); override with `className="text-..."` if needed.
 *
 * Mirrors SFRA's `.border-wavy-t` (see `org_hec/.../utilities/_u-border.scss`).
 */
export const WavyDivider = React.forwardRef<SVGSVGElement, WavyDividerProps>(
  ({ width = 139, height = 5, className, style, ...props }, ref) => (
    <svg
      ref={ref}
      width={width}
      height={height}
      viewBox={`0 0 148 ${height}`}
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      style={{ width: `${width}px`, height: `${height}px`, ...style }}
      className={cn("block text-wavy", className)}
      {...props}
    >
      <path
        fill="currentColor"
        d="M5.398,2.087c1.907-1.783,5.507-1.783,7.413,0c0.947,0.884,3.055,0.884,4.002,0c1.908-1.783,5.508-1.783,7.415,0 c0.947,0.884,3.055,0.884,4.001,0c1.908-1.783,5.508-1.783,7.416,0c0.946,0.884,3.055,0.884,4.001,0 c1.907-1.783,5.507-1.783,7.415,0c0.947,0.884,3.055,0.884,4.002,0c1.907-1.783,5.507-1.783,7.414,0 c0.947,0.884,3.055,0.884,4.002,0c1.908-1.783,5.507-1.783,7.416,0c0.946,0.884,3.054,0.884,4.001,0 c1.907-1.783,5.508-1.783,7.414,0c0.947,0.884,3.055,0.884,4.002,0c1.907-1.783,5.508-1.783,7.416,0c0.945,0.884,3.055,0.884,4,0 c1.907-1.783,5.508-1.783,7.416,0c0.946,0.884,3.055,0.884,4.002,0c1.907-1.783,5.506-1.783,7.414,0 c0.947,0.884,3.055,0.884,4.002,0c1.907-1.783,5.506-1.783,7.414,0c0.947,0.884,3.055,0.884,4.002,0 c1.907-1.783,5.508-1.783,7.414,0c0.904,0.844,2.867,0.882,3.867,0.115l0.137-0.115c0.504-0.472,1.295-0.445,1.766,0.06 c0.472,0.504,0.445,1.295-0.061,1.766c-1.906,1.783-5.508,1.783-7.416,0c-0.945-0.884-3.054-0.884-4,0 c-1.906,1.783-5.508,1.783-7.416,0c-0.947-0.884-3.054-0.884-4,0c-1.908,1.783-5.508,1.783-7.416,0c-0.947-0.884-3.055-0.884-4,0 c-1.908,1.783-5.509,1.783-7.416,0c-0.946-0.884-3.055-0.884-4.002,0c-1.906,1.783-5.508,1.783-7.414,0 c-0.946-0.884-3.056-0.884-4.002,0c-1.906,1.783-5.508,1.783-7.416,0c-0.945-0.884-3.054-0.884-4,0 c-1.908,1.783-5.507,1.783-7.416,0c-0.947-0.884-3.055-0.884-4.001,0c-1.908,1.783-5.508,1.783-7.415,0 c-0.947-0.884-3.055-0.884-4.002,0c-1.907,1.783-5.507,1.783-7.414,0c-0.947-0.884-3.056-0.884-4.002,0 c-1.908,1.783-5.508,1.783-7.415,0c-0.947-0.884-3.055-0.884-4.002,0c-1.907,1.783-5.507,1.783-7.415,0 c-0.946-0.884-3.055-0.884-4.001,0c-1.908,1.783-5.508,1.783-7.415,0c-0.904-0.844-2.866-0.882-3.866-0.115L7.103,3.913 c-0.504,0.472-1.295,0.445-1.766-0.06C4.865,3.349,4.892,2.558,5.398,2.087L5.398,2.087z"
      />
    </svg>
  ),
);

WavyDivider.displayName = "WavyDivider";
